import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Instagram, AtSign, Facebook, Youtube, Globe, ArrowUpRight, UserPlus } from "lucide-react";
import { Logo } from "../components/brand/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Palette — matches gadgetguymedia.com homepage
const BG = "#0B1E3A";           // main background
const SURFACE = "#0F2547";       // card surface
const ACCENT = "#28C8F0";        // signal cyan (formerly gold)
const ACCENT_HOVER = "#7de0f7";  // brighter cyan for hover
const ACCENT_SOFT = "rgba(40,200,240,0.35)";
const ACCENT_BG = "rgba(40,200,240,0.08)";
const ACCENT_BG_HOVER = "rgba(40,200,240,0.16)";
const PRIMARY_GRADIENT = "linear-gradient(135deg, #0B3CDC 0%, #28C8F0 100%)";
const PRIMARY_GRADIENT_HOVER = "linear-gradient(135deg, #0d47ff 0%, #52d6f5 100%)";

const XIcon = ({ size = 20, color = "currentColor" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill={color} aria-hidden="true">
    <path d="M18.244 2H21.5l-7.5 8.573L23 22h-6.9l-5.4-6.72L4.6 22H1.34l8.02-9.166L1 2h7.05l4.88 6.16L18.244 2Zm-1.21 18h1.87L7.05 4H5.1l11.93 16Z" />
  </svg>
);

const socials = [
  { id: "instagram", label: "Instagram", url: "https://www.instagram.com/gadgetguymedia", Icon: Instagram },
  { id: "threads", label: "Threads", url: "https://www.threads.com/@gadgetguymedia", Icon: AtSign },
  { id: "facebook", label: "Facebook", url: "https://www.facebook.com/gadgetguymedia", Icon: Facebook },
  { id: "x", label: "X", url: "https://x.com/gadgetguymedia", Icon: XIcon },
  { id: "youtube", label: "YouTube", url: "https://youtube.com/@gadgetguymedia", Icon: Youtube },
];

export default function RobertPage() {
  const pinged = useRef(false);

  // Fire a single scan-tracking ping on mount so we can see traffic
  useEffect(() => {
    if (pinged.current) return;
    pinged.current = true;
    const src = new URLSearchParams(window.location.search).get("src");
    axios
      .post(`${API}/track/robert-scan`, {
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
        source: src || null,
      })
      .catch(() => {}); // silent — never disrupt the page
  }, []);

  // Track a tap on the main (bottom) QR — lets us compare header vs main scan performance
  const trackMainQrTap = () => {
    axios
      .post(`${API}/track/robert-scan`, {
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
        source: "main-qr-tap",
      })
      .catch(() => {});
  };

  // Per-page SEO meta
  useEffect(() => {
    document.title = "Robert — Founder, Gadget Guy Media";
    const setMeta = (attr, key, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    const setLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };
    const desc =
      "Robert — Founder of Gadget Guy Media. AI apps that ship, delivered in weeks, not months. Scan, save the contact card, or connect to see what's next.";
    setMeta("name", "description", desc);
    setMeta("property", "og:title", "Robert — Founder, Gadget Guy Media");
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", "https://gadgetguymedia.com/robert");
    setMeta("name", "twitter:title", "Robert — Founder, Gadget Guy Media");
    setMeta("name", "twitter:description", desc);
    setLink("canonical", "https://gadgetguymedia.com/robert");
    return () => setLink("canonical", "https://gadgetguymedia.com/");
  }, []);


  return (
    <div
      style={{
        minHeight: "100vh",
        background: BG,
        color: "#ffffff",
        fontFamily: '"Roboto", -apple-system, BlinkMacSystemFont, sans-serif',
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "20px 16px 32px",
      }}
      data-testid="robert-page"
    >
      {/* Brand header — approved Logo, links home */}
      <div style={{ width: "100%", maxWidth: 460, display: "flex", justifyContent: "center", marginBottom: 20 }}>
        <Link to="/" aria-label="Gadget Guy Media home" data-testid="robert-brand-link">
          <Logo variant="horizontal" height={40} />
        </Link>
      </div>
      <main
        style={{
          width: "100%",
          maxWidth: 460,
          background: SURFACE,
          border: "1px solid rgba(40,200,240,0.18)",
          borderRadius: 22,
          padding: "36px 28px",
          boxShadow: "0 30px 80px -20px rgba(0,0,0,0.55)",
        }}
      >
        {/* Header */}
        <section style={{ textAlign: "center", marginBottom: 28 }}>
          <div
            style={{
              width: 128,
              height: 128,
              borderRadius: "50%",
              margin: "0 auto 20px",
              overflow: "hidden",
              border: `3px solid ${ACCENT}`,
              boxShadow: "0 12px 40px -12px rgba(40,200,240,0.55)",
            }}
          >
            <img
              src="/brand/robert-studio.png"
              alt="Robert — founder of Gadget Guy Media"
              style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "55% 30%" }}
              data-testid="robert-headshot"
            />
          </div>
          <h1
            style={{
              fontFamily: '"Roboto Condensed", "Roboto", sans-serif',
              fontWeight: 700,
              fontSize: 34,
              lineHeight: 1.05,
              letterSpacing: "-0.01em",
              margin: 0,
              color: "#ffffff",
            }}
            data-testid="robert-name"
          >
            Robert
          </h1>
          <p
            style={{
              fontFamily: '"Roboto Condensed", sans-serif',
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.16em",
              fontSize: 12,
              color: ACCENT,
              margin: "10px 0 0 0",
            }}
            data-testid="robert-title"
          >
            Founder · Gadget Guy Media
          </p>
          <p
            style={{
              margin: "14px auto 0",
              maxWidth: 320,
              color: "rgba(255,255,255,0.75)",
              fontSize: 15,
              lineHeight: 1.45,
            }}
            data-testid="robert-tagline"
          >
            AI apps that ship.
            <br />
            Delivered in weeks, not months.
          </p>
        </section>

        {/* Main link */}
        <a
          href="https://gadgetguymedia.com"
          target="_blank"
          rel="noreferrer"
          data-testid="robert-main-link"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            width: "100%",
            background: ACCENT,
            color: BG,
            fontFamily: '"Roboto Condensed", sans-serif',
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            padding: "16px 22px",
            borderRadius: 14,
            textDecoration: "none",
            marginBottom: 22,
            transition: "background 180ms, transform 180ms",
            fontSize: 15,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = ACCENT_HOVER)}
          onMouseLeave={(e) => (e.currentTarget.style.background = ACCENT)}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
            <Globe size={20} strokeWidth={2.4} />
            Gadget Guy Media
          </span>
          <ArrowUpRight size={20} strokeWidth={2.4} />
        </a>

        {/* Socials */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 10,
            marginBottom: 32,
          }}
          data-testid="robert-socials"
        >
          {socials.map((s) => {
            const Icon = s.Icon;
            return (
              <a
                key={s.id}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                aria-label={s.label}
                data-testid={`robert-social-${s.id}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  aspectRatio: "1 / 1",
                  borderRadius: 12,
                  background: "rgba(40,200,240,0.08)",
                  border: "1px solid rgba(40,200,240,0.35)",
                  color: ACCENT,
                  transition: "background 180ms, border-color 180ms, transform 180ms",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(40,200,240,0.16)";
                  e.currentTarget.style.borderColor = ACCENT;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(40,200,240,0.08)";
                  e.currentTarget.style.borderColor = "rgba(40,200,240,0.35)";
                }}
              >
                <Icon size={20} color={ACCENT} />
              </a>
            );
          })}
        </div>

        {/* Save To Contacts — points visitors to dot.cards where they can save the card
            and where lead capture + tracking happens */}
        <a
          href="https://dot.cards/gadgetguymedia"
          target="_blank"
          rel="noreferrer"
          data-testid="robert-vcard-btn"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            background: PRIMARY_GRADIENT,
            color: "#ffffff",
            fontFamily: '"Roboto Condensed", sans-serif',
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            padding: "16px 22px",
            borderRadius: 14,
            textDecoration: "none",
            marginBottom: 32,
            transition: "background 200ms, transform 200ms, box-shadow 200ms",
            fontSize: 15,
            boxShadow: "0 10px 30px -10px rgba(11,60,220,0.55)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = PRIMARY_GRADIENT_HOVER;
            e.currentTarget.style.transform = "translateY(-1px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = PRIMARY_GRADIENT;
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
            <UserPlus size={20} strokeWidth={2.4} />
            Save To Contacts
          </span>
          <ArrowUpRight size={20} strokeWidth={2.4} />
        </a>

        {/* QR Code — branded, encodes dot.cards/gadgetguymedia */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: 32,
          }}
          data-testid="robert-qr"
        >
          <div
            style={{
              background: "#ffffff",
              padding: 10,
              borderRadius: 18,
              boxShadow: "0 8px 24px -8px rgba(0,0,0,0.5)",
            }}
          >
            <a
              href="https://dot.cards/gadgetguymedia"
              target="_blank"
              rel="noreferrer"
              aria-label="Open dot.cards/gadgetguymedia"
              data-testid="robert-qr-link"
              onClick={trackMainQrTap}
              onAuxClick={trackMainQrTap}
            >
              <img
                src="/brand/dotcode.png"
                alt="QR code — scan to open dot.cards/gadgetguymedia"
                style={{ width: 220, height: "auto", display: "block", borderRadius: 8 }}
                draggable={false}
              />
            </a>
          </div>
          <p
            style={{
              fontFamily: '"Roboto Condensed", sans-serif',
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.24em",
              fontSize: 11,
              color: ACCENT,
              margin: "14px 0 0 0",
            }}
          >
            Scan to connect
          </p>
        </div>

        <p
          style={{
            margin: "26px 0 0 0",
            textAlign: "center",
            fontSize: 11,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.35)",
            fontFamily: '"Roboto Condensed", sans-serif',
          }}
        >
          gadgetguymedia.com/robert
        </p>
      </main>
    </div>
  );
}
